// [PURPOSE]: Phase B / #044 — apply `levelProgression` entries with
//   `action: 'autoStatBoost'` to a level-up commit object.
//
// [WHY]: Pre-fix, App.jsx hardcoded the Barbarian L20 Primal Champion
//   stat bump (+4 STR / +4 CON, cap 24) inside handleLevelUpComplete.
//   That's a string-domain match (`className === 'Barbarian'`) plus a
//   magic level number. The data block lives on the class itself
//   (SRD_CLASSES.Barbarian.levelProgression), and this helper iterates
//   the entries that fire at the level being committed.
//
// [SCHEMA]: Each entry on the class's levelProgression array:
//   {
//     atLevel: number,       // class level the boost fires at
//     action:  'autoStatBoost',
//     stat:    'strength' | 'dexterity' | 'constitution' | ...,
//     amount:  number,       // amount to add (typically positive)
//     cap:     number,       // ceiling — boost result is min(current+amount, cap)
//   }
//
// [INTEGRATION]: App.jsx handleLevelUpComplete calls this with the
//   `updated` playerStats draft, the class being leveled, and the
//   class level being reached. The helper:
//     - reads the class's levelProgression array via contentResolver,
//     - filters to entries where atLevel === classLevel and
//       action === 'autoStatBoost',
//     - mutates `updated[stat]` in place (capped),
//     - mutates `updated.statCaps[stat]` to the cap value.
//   Return value is the mutated `updated` object for caller chaining.

import { getClassData } from './contentResolver';

/**
 * @param {object} updated - Mutable playerStats draft.
 * @param {string} className - The class being leveled up.
 * @param {number} classLevel - The class level just reached.
 * @returns {object} The mutated `updated` object.
 */
export function applyLevelProgressionAutoStatBoosts(updated, className, classLevel) {
  if (!updated || typeof className !== 'string' || typeof classLevel !== 'number') {
    return updated;
  }
  const classData = getClassData(className);
  const progression = Array.isArray(classData?.levelProgression)
    ? classData.levelProgression
    : [];
  const boosts = progression.filter(
    (e) => e && e.atLevel === classLevel && e.action === 'autoStatBoost'
  );
  if (boosts.length === 0) return updated;

  for (const boost of boosts) {
    const stat = boost.stat;
    const amount = Number(boost.amount) || 0;
    const cap = Number(boost.cap);
    if (!stat) continue;

    const current = typeof updated[stat] === 'number' ? updated[stat] : 10;
    // Apply the boost, but cap the RESULT at `cap`. If `current` is
    // already above `cap` (e.g., homebrew started high), don't lower
    // them — keep the existing value.
    const boosted = current + amount;
    const next = Number.isFinite(cap) ? Math.min(boosted, cap) : boosted;
    updated[stat] = current >= next ? current : next;

    if (Number.isFinite(cap)) {
      updated.statCaps = { ...(updated.statCaps || {}), [stat]: cap };
    }
  }

  return updated;
}
